import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { identifiers } from '../../../utils/constants/identifiers';
import { colors } from '../../../utils/constants/data';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

export type VenueOccupancyReportRow = {
  venueName?: string;
  availableDays?: number;
  bookedDays?: number;
  unbookedDays?: number;
  occupancy?: string;
  bookedDates?: string[];
  unavailableDates?: string[];
};

type InsightStyle = 'green' | 'success' | 'blue' | 'yellow' | 'red';

type VenueOccupancyReportData = {
  name?: string;
  reportType?: string;
  rows?: VenueOccupancyReportRow[];
  kpiMap?: Record<string, string>;
  infoText?: string;
  insightText?: string;
  insightStyle?: InsightStyle;
  startDate?: string;
  endDate?: string;
};

interface Props {
  data: VenueOccupancyReportData;
  name: string;
  handleExport?: () => void;
  showButton?: boolean;
}

function formatCount(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
}

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  borderBottom: '1px solid #E5E7EB',
  py: 1,
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  fontSize: '13px',
  color: '#1F2937',
  borderBottom: '1px solid #F1F5F9',
  py: 1.1,
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CALENDAR_MAX_WIDTH = 360;
const EMPTY_CELL = { bg: '#F9FAFB', color: '#374151', border: '#E5E7EB' };
const BOOKED_CELL = { bg: '#E8F5E9', color: '#1B5E20', border: '#43A047' };
const UNAVAILABLE_CELL = { bg: colors.UNAVAILABLE, color: '#333333', border: '#E35D6A' };

type DayStatus = 'booked' | 'open' | 'unavailable' | 'outside';

function monthCells(month: Dayjs): Array<number | null> {
  const first = month.startOf('month');
  const leading = (first.day() + 6) % 7;
  const cells: Array<number | null> = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= month.daysInMonth(); day += 1) {
    cells.push(day);
  }
  return cells;
}

const VenueOccupancyCalendar = ({
  row,
  startDate,
  endDate,
  onClose,
}: {
  row: VenueOccupancyReportRow;
  startDate?: string;
  endDate?: string;
  onClose: () => void;
}) => {
  const booked = useMemo(() => new Set(row.bookedDates || []), [row.bookedDates]);
  const unavailable = useMemo(() => new Set(row.unavailableDates || []), [row.unavailableDates]);
  const rangeStart = dayjs(startDate);
  const rangeEnd = dayjs(endDate);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    (rangeStart.isValid() ? rangeStart : dayjs()).startOf('month'),
  );
  const cells = useMemo(() => monthCells(visibleMonth), [visibleMonth]);

  const statusFor = (date: Dayjs): DayStatus => {
    if (!rangeStart.isValid() || !rangeEnd.isValid()) return 'outside';
    if (date.isBefore(rangeStart, 'day') || date.isAfter(rangeEnd, 'day')) return 'outside';
    const iso = date.format('YYYY-MM-DD');
    if (unavailable.has(iso)) return 'unavailable';
    if (booked.has(iso)) return 'booked';
    return 'open';
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 444 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '18px' }}>
          {row.venueName}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 1, pb: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mb: 1.5,
              width: '100%',
              maxWidth: CALENDAR_MAX_WIDTH,
            }}>
            <IconButton
              size="small"
              aria-label="Previous month"
              onClick={() => setVisibleMonth(month => month.subtract(1, 'month').startOf('month'))}>
              <ChevronLeftIcon />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                views={['month', 'year']}
                openTo="month"
                inputFormat={identifiers.month_input}
                value={visibleMonth}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) setVisibleMonth(newValue.startOf('month'));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                      'aria-label': 'Select month',
                    }}
                    sx={{
                      flex: '1 1 auto',
                      width: 'auto',
                      maxWidth: 168,
                      minWidth: 0,
                      '& .MuiInputBase-root': { fontWeight: 700, fontSize: '0.9rem' },
                      '& .MuiInputBase-input': { textAlign: 'center', cursor: 'pointer', py: 0.75 },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
            <IconButton
              size="small"
              aria-label="Next month"
              onClick={() => setVisibleMonth(month => month.add(1, 'month').startOf('month'))}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
              width: '100%',
              maxWidth: CALENDAR_MAX_WIDTH,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, border: '1px solid #43A047', bgcolor: '#E8F5E9' }} />
              <Typography variant="caption" color="text.secondary">
                Booked
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, border: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }} />
              <Typography variant="caption" color="text.secondary">
                Unbooked
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  border: '1px solid',
                  borderColor: '#E35D6A',
                  bgcolor: colors.UNAVAILABLE,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Unavailable
              </Typography>
            </Box>
          </Box>

          <Box sx={{ width: '100%', maxWidth: CALENDAR_MAX_WIDTH }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
              {WEEKDAYS.map(label => (
                <Typography
                  key={label}
                  variant="caption"
                  sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem' }}>
                  {label}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {cells.map((day, index) => {
                if (day == null) {
                  return <Box key={`empty-${index}`} sx={{ aspectRatio: '1', minHeight: 40 }} />;
                }
                const date = visibleMonth.date(day);
                const status = statusFor(date);
                const cell =
                  status === 'booked' ? BOOKED_CELL : status === 'unavailable' ? UNAVAILABLE_CELL : EMPTY_CELL;
                return (
                  <Box
                    key={date.format('YYYY-MM-DD')}
                    sx={{
                      aspectRatio: '1',
                      minHeight: 40,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: cell.border,
                      bgcolor: cell.bg,
                      color: status === 'outside' ? '#D1D5DB' : cell.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.1 }}>
                      {day}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const VenueOccupancyReportTable: React.FC<Props> = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name || 'Venue Occupancy';
  const rows = data?.rows || [];
  const [selectedRow, setSelectedRow] = useState<VenueOccupancyReportRow | null>(null);

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader title={displayName} insight={data?.infoText} handleExport={handleExport} showButton={showButton} />
      <ReportKpiStrip items={kpiMapToItems(data?.kpiMap) || []} />
      {data?.insightText && <ReportInsightBanner text={data.insightText} style={data.insightStyle} />}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mt: 0.5 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              color: '#94A3B8',
            }}>
            <Typography variant="body2">No venue occupancy data for the selected period.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Venue</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Available
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Booked
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Unbooked
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Occupancy
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.venueName || 'row'}-${index}`} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                      {row.venueName ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedRow(row)}
                          sx={{
                            p: 0,
                            border: 0,
                            bgcolor: 'transparent',
                            color: 'primary.main',
                            font: 'inherit',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'left',
                            '&:hover': { textDecoration: 'underline' },
                          }}>
                          {row.venueName}
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(row.availableDays)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(row.bookedDays)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(row.unbookedDays)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {row.occupancy || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {selectedRow && (
        <VenueOccupancyCalendar
          row={selectedRow}
          startDate={data?.startDate}
          endDate={data?.endDate}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </Paper>
  );
};

export default VenueOccupancyReportTable;
